/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import { ACTIVITY_STATUS, useDateTime } from "@/shared";
import { useNow } from "@/shared/hooks/useNow";
import { useRelativeDateTime } from "@/shared/hooks/useRelativeDateTime";
import { Typography } from "@mui/material";

import {
  Badge,
  BadgeColors,
  BadgeOwnProps,
  colors
} from "@dotnet/telematics-design-system";

import { DownloadMonitoringVehicleData } from "../types/interfaces";
import DescriptionItem from "./DescriptionItem";

interface DownloadMonitoringDescriptionProps {
  liveStatusData: DownloadMonitoringVehicleData;
}

const DownloadMonitoringDescription = ({
  liveStatusData
}: DownloadMonitoringDescriptionProps) => {
  const { t } = useTranslation();

  const { currentTimeFormatHoursMinutes, formattedDate } = useDateTime();

  const { formatRelativeDateTime } = useRelativeDateTime();

  const {
    eligibility: { lastConnected, deviceStatus, imei, companyName }
  } = liveStatusData || {};

  const now = useNow(60_000);

  const items = [
    {
      label: t("Table##last connection"),
      value: formatRelativeDateTime(lastConnected, now),
      tooltip: formattedDate(lastConnected, currentTimeFormatHoursMinutes)
    },
    {
      label: t("Table##imei"),
      value: imei ?? "-"
    },
    {
      label: t("Table##company"),
      value: companyName
    }
  ];

  const getBubbleData = (statusName: ACTIVITY_STATUS): BadgeOwnProps => {
    switch (statusName) {
      case ACTIVITY_STATUS.Online:
        return { color: BadgeColors.success, inverted: true };

      case ACTIVITY_STATUS.Offline:
        return { color: BadgeColors.warning, inverted: true };

      default:
        return { color: BadgeColors.light, inverted: false };
    }
  };

  const badgeColor = getBubbleData(deviceStatus)?.color;
  const isInverted = getBubbleData(deviceStatus)?.inverted;

  return (
    <Typography
      variant="body3Medium"
      component="div"
      css={css({
        display: "flex",
        flexWrap: "wrap",
        gap: "8px 16px"
      })}
    >
      <div
        css={css({
          display: "flex",
          alignItems: "center"
        })}
      >
        <span css={css({ color: colors.textPlaceholder })}>
          {t("Details##device status")}:
        </span>

        {deviceStatus ? (
          <>
            <Badge
              color={badgeColor}
              inverted={isInverted}
              style={{
                margin: "2px 4px 0 4px",
                padding: "3px",
                borderRadius: "100%"
              }}
            />

            <span>{t(`General##${deviceStatus}`)}</span>
          </>
        ) : (
          <div
            style={{
              marginLeft: "4px"
            }}
          >
            -
          </div>
        )}
      </div>

      {items.map(({ label, value, tooltip }) => (
        <DescriptionItem key={label} label={label} tooltipText={tooltip}>
          {value}
        </DescriptionItem>
      ))}
    </Typography>
  );
};

export default DownloadMonitoringDescription;
